/**
 * PLAT-FIRM-LOGO-1 — a firm/brand logo that never shows as a broken image.
 *
 * No logo, or a logo that fails to load (deleted, blocked, an old fake URL),
 * renders the firm/brand NAME as text instead. Pass `fallback={null}` where
 * the name is already on screen next to the logo.
 *
 * DEDUP-1 (c): the one copy for both apps. scripts/sync_shared_ts.py mirrors it
 * into apps/otcid_react/src/utils/. OTCID styles with inline `style`, this app
 * with `className`; both are accepted and passed through unchanged.
 */
import { useState, type CSSProperties, type ReactNode } from 'react'

/** Uploaded logos are stored as "/api/v1/logos/<uuid>.<ext>"; serve them from the API host. */
export function resolveLogoSrc(ref?: string | null): string {
  if (!ref) return ''
  return ref.startsWith('/api/') ? (import.meta.env.VITE_API_BASE_URL ?? '') + ref : ref
}

interface Props {
  src?: string | null
  name: string
  className?: string
  style?: CSSProperties
  fallbackClassName?: string
  /** Given without `fallbackClassName`, replaces the default fallback class. */
  fallbackStyle?: CSSProperties
  fallback?: ReactNode
}

export function LogoImage({
  src, name, className, style, fallbackClassName, fallbackStyle, fallback,
}: Props) {
  const url = resolveLogoSrc(src)
  const [failedUrl, setFailedUrl] = useState<string | null>(null)

  if (!url || failedUrl === url) {
    if (fallback !== undefined) return <>{fallback}</>
    return (
      <span
        data-testid="logo-fallback"
        className={fallbackClassName ?? (fallbackStyle ? undefined : 'text-sm font-semibold')}
        style={fallbackStyle}
      >
        {name}
      </span>
    )
  }
  return <img src={url} alt={name} className={className} style={style} onError={() => setFailedUrl(url)} />
}
